import { ChainConnectivityWarning } from './ChainConnectivityWarning';
import { getChainInfo } from 'constants/chainInfo';
import ms from 'ms';
import { useEffect, useMemo, useState } from 'react';

import { cn } from 'utils/cn';
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink';

import useCurrentBlockTimestamp from 'hooks/useCurrentBlockTimestamp';
import useMachineTimeMs from 'hooks/useMachineTime';
import { useWeb3React } from 'hooks/useWeb3React';
import useBlockNumber from 'lib/hooks/useBlockNumber';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/Tooltip';

import Spinner from 'components/Icons/Spinner';
import { ExternalLink } from 'components/composed/ExternalLink';

const DEFAULT_MS_BEFORE_WARNING = ms('10m');
const NETWORK_HEALTH_CHECK_MS = ms('10s');

const Polling = () => {
  const { chainId } = useWeb3React();
  const blockNumber = useBlockNumber();
  const [isMounting, setIsMounting] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const machineTime = useMachineTimeMs(NETWORK_HEALTH_CHECK_MS);
  const blockTime = useCurrentBlockTimestamp();

  const waitMsBeforeWarning =
    (chainId
      ? getChainInfo(chainId)?.blockWaitMsBeforeWarning
      : DEFAULT_MS_BEFORE_WARNING) ?? DEFAULT_MS_BEFORE_WARNING;

  const warning = Boolean(
    !!blockTime &&
      machineTime - blockTime.mul(1000).toNumber() > waitMsBeforeWarning
  );

  useEffect(
    () => {
      if (!blockNumber) {
        return;
      }

      setIsMounting(true);
      const mountingTimer = setTimeout(() => setIsMounting(false), 1000);

      // this will clear Timeout when component unmount like in willComponentUnmount
      return () => {
        clearTimeout(mountingTimer);
      };
    },
    [blockNumber] //useEffect will run only one time
    //if you pass a value to array, like this [data] than clearTimeout will run every time this value changes (useEffect re-run)
  );

  //TODO - chainlink gas oracle is really slow. Can we get a better data source?

  const blockExternalLinkHref = useMemo(() => {
    if (!chainId || !blockNumber) return '';
    return getExplorerLink({
      chainId,
      data: blockNumber.toString(),
      type: ExplorerDataType.BLOCK
    });
  }, [blockNumber, chainId]);

  return (
    <div className='flex w-fit items-center justify-start'>
      <div
        className='fixed bottom-0 right-0 hidden items-center p-4 text-gray-600 lg:flex'
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <span
          className={cn(
            'text-xs font-medium transition-opacity duration-300',
            'hover:opacity-100',
            {
              'opacity-70': isHover,
              'opacity-100': isMounting,
              'opacity-50': !isMounting && !isHover,
              'text-yellow': warning,
              'text-green': !warning
            }
          )}
        >
          <ExternalLink href={blockExternalLinkHref}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger className='cursor-default' asChild>
                <div className='cursor-default font-bold text-gray-700/50'>
                  {blockNumber}&ensp;
                </div>
              </TooltipTrigger>
              <TooltipContent className='max-w-80 bg-blue-700' side='bottom'>
                The most recent block number on this network. Prices update on
                every block.
              </TooltipContent>
            </Tooltip>
          </ExternalLink>
        </span>
        <div
          className={cn(
            'relative size-2 min-h-2 min-w-2 rounded-full transition-colors duration-300',
            {
              'bg-yellow': warning,
              'bg-green': !warning
            }
          )}
        >
          {isMounting && (
            <Spinner
              className={cn('size-4', {
                'stroke-yellow': warning,
                'stroke-green': !warning
              })}
            />
          )}
        </div>{' '}
      </div>
      {warning && <ChainConnectivityWarning />}
    </div>
  );
};

export default Polling;
